import React, { useState, useEffect } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { useT } from '../../i18n';
import LanguageSwitcher from '../shared/LanguageSwitcher';
import NotificationBell from '../shared/NotificationBell';
import useFullscreen from '../../hooks/useFullscreen';
import useFavicon from '../../hooks/useFavicon';
import useVersionCheck from '../../hooks/useVersionCheck';
import versionInfo from '../../version.json';
import api from '../../utils/api';

const NAV_TOP = [
  { to: '/',               icon: '⊞', key: 'dashboard' },
];

// Collapsible "Documents" group — every document type plus the combined list.
// Labels come from docs.allDocuments / docs.titlePlural.<type> (already translated).
const DOC_NAV = [
  { to: '/documents',       icon: '📁', type: null },
  { to: '/quotations',      icon: '📋', type: 'quotation' },
  { to: '/invoices',        icon: '🧾', type: 'invoice' },
  { to: '/receipts',        icon: '✅', type: 'receipt' },
  { to: '/bills',           icon: '💳', type: 'bill' },
  { to: '/purchase-orders', icon: '🛒', type: 'purchase_order' },
  { to: '/delivery-orders', icon: '🚚', type: 'delivery_order' },
  { to: '/credit-notes',    icon: '↩',  type: 'credit_note' },
  { to: '/statements',      icon: '📑', type: 'statement' },
];

const NAV_REST = [
  { to: '/recurring',      icon: '🔁', key: 'recurring' },
  { to: '/clients',        icon: '👥', key: 'clients' },
  { to: '/items',          icon: '📦', key: 'items' },
  { to: '/settings',       icon: '⚙',  key: 'settings' },
  { to: '/subscription',   icon: '💎', key: 'subscription' },
  { to: '/support',        icon: '🎧', key: 'support' },
];

const ADMIN_NAV = [
  { to: '/admin/stats',      icon: '📊', key: 'system' },
  { to: '/admin/users',      icon: '🛡', key: 'users' },
  { to: '/admin/support',    icon: '🎧', key: 'supportCases' },
  { to: '/admin/user-stats', icon: '📈', key: 'userStats' },
  { to: '/admin/finance',    icon: '💰', key: 'finance' },
  { to: '/admin/activity',   icon: '📜', key: 'activity' },
  { to: '/admin/backups',    icon: '💾', key: 'backups' },
  { to: '/admin/changelog',  icon: '🏷', key: 'changelog' },
];

const MOBILE_BREAKPOINT = 768;

function useViewport() {
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' && window.innerWidth <= MOBILE_BREAKPOINT
  );
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= MOBILE_BREAKPOINT);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return { isMobile };
}

export default function Layout({ children }) {
  const { user, logout, updateUser } = useAuth();
  const { pathname } = useLocation();
  const { theme, toggle: toggleTheme } = useTheme();
  const { t, isRTL } = useT();
  const { isFullscreen, toggle: toggleFullscreen } = useFullscreen();
  const navigate = useNavigate();
  const { isMobile } = useViewport();

  const [collapsed, setCollapsed] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Trial grace period: trial has ended but the 5-day grace window is still open
  const trialEnded = user?.subscriptionStatus === 'trialing' && user?.trialEndsAt
    && new Date(user.trialEndsAt) <= Date.now();
  const graceEnds = user?.graceEndsAt ? new Date(user.graceEndsAt) : null;
  const inGrace = trialEnded && graceEnds && graceEnds > Date.now();
  const graceDaysLeft = inGrace ? Math.max(1, Math.ceil((graceEnds - Date.now()) / 86400000)) : 0;

  // Payment failed — subscription is past_due, user keeps access during pay-grace
  const isPastDue = user?.subscriptionStatus === 'past_due';
  const payGraceDaysLeft = isPastDue
    ? (user?.payGraceEndsAt
        ? Math.max(0, Math.ceil((new Date(user.payGraceEndsAt) - Date.now()) / 86400000))
        : 7)
    : 0;
  const openBillingPortal = async () => {
    try {
      const { data } = await api.post('/stripe/portal');
      window.location.href = data.url;
    } catch {
      navigate('/subscription');
    }
  };

  // One-off notice set by the server (e.g. admin extended the trial)
  const notice = user?.notice;
  const dismissNotice = () => {
    updateUser({ notice: null }); // optimistic — banner disappears immediately
    api.post('/auth/notice/dismiss').catch(() => {});
  };

  const [logoUrl, setLogoUrl] = useState(null);
  useEffect(() => {
    let cancelled = false;
    api.get('/company').then(r => { if (!cancelled && r.data?.logoUrl) setLogoUrl(r.data.logoUrl); }).catch(() => {});
    return () => { cancelled = true; };
  }, []);
  useFavicon(logoUrl);

  const { latest, updateAvailable } = useVersionCheck();
  const reloadForUpdate = () => window.location.reload();

  const [resending, setResending] = useState(false);
  const [resentMsg, setResentMsg] = useState('');
  const resendVerification = async () => {
    setResending(true); setResentMsg('');
    try {
      await api.post('/auth/resend-verification');
      setResentMsg(t('common.loading'));
    } catch (e) {
      setResentMsg(e.response?.data?.message || 'Failed to send verification email.');
    } finally { setResending(false); }
  };

  // Admin: cases awaiting a reply, shown as a badge on the Support Cases nav item
  const [supportCount, setSupportCount] = useState(0);
  useEffect(() => {
    if (user?.role !== 'admin') return;
    api.get('/admin/support/count')
      .then(r => setSupportCount(r.data.needsReply || 0))
      .catch(() => {});
  }, [user?.role, pathname]);

  // Unread-notification dot on the mobile ☰ button — NotificationBell
  // broadcasts its count so we don't fetch twice.
  const [mobileUnread, setMobileUnread] = useState(0);
  useEffect(() => {
    const onUnread = e => setMobileUnread(e.detail || 0);
    window.addEventListener('invio:unread', onUnread);
    return () => window.removeEventListener('invio:unread', onUnread);
  }, []);

  // Admin support session: the admin's own token was parked in localStorage
  // when they hit "Login as" on the Users page.
  const impersonating = !!localStorage.getItem('adminToken');
  const endImpersonation = () => {
    localStorage.setItem('token', localStorage.getItem('adminToken'));
    localStorage.removeItem('adminToken');
    window.location.href = '/admin/users';
  };

  const closeDrawer = () => setDrawerOpen(false);

  const initials = (user?.name || '?').split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);
  const handleLogout = () => { logout(); navigate('/login'); };

  const sidebarWidth = isMobile ? 260 : (collapsed ? 64 : 220);
  const sidebarCollapsed = isMobile ? false : collapsed;

  const sidebar = (
    <aside style={{
      width: sidebarWidth,
      background: 'var(--sidebar-bg)',
      display: 'flex', flexDirection: 'column',
      transition: 'width 0.2s ease, transform 0.25s ease',
      flexShrink: 0,
      position: isMobile ? 'fixed' : 'sticky',
      top: 0, left: 0, height: '100vh', overflowX: 'hidden',
      zIndex: 1001,
      transform: isMobile && !drawerOpen ? 'translateX(-100%)' : 'translateX(0)',
      boxShadow: isMobile ? '4px 0 20px rgba(0,0,0,0.3)' : 'none',
    }}>
      {/* Logo */}
      <div style={{ padding: '20px 16px 16px', borderBottom: '1px solid var(--sidebar-border)' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 0 }}>
          {sidebarCollapsed ? (
            <span style={{ fontSize: 22, fontWeight: 900, color: '#F59E0B', letterSpacing: '-1px', lineHeight: 1 }}>I</span>
          ) : (
            <div>
              <div style={{ lineHeight: 1.15 }}>
                <span style={{ fontSize: 22, fontWeight: 900, color: '#F59E0B', letterSpacing: '-1px' }}>I</span>
                <span style={{ fontSize: 22, fontWeight: 700, color: '#ffffff', letterSpacing: '-0.5px' }}>nvio</span>
              </div>
              <span style={{ color: 'var(--sidebar-text-muted)', fontSize: 10 }}>v{versionInfo.version} · invio.my</span>
            </div>
          )}
          {isMobile && (
            <button onClick={closeDrawer} style={{
              background: 'none', border: 'none', color: '#fff', cursor: 'pointer',
              fontSize: 22, padding: 4, lineHeight: 1
            }} aria-label="Close menu">×</button>
          )}
        </div>
      </div>

      {/* Nav */}
      <nav style={{ flex: 1, padding: '12px 8px', overflowY: 'auto' }}>
        {NAV_TOP.map(item => <NavItem key={item.to} item={item} label={t(`nav.${item.key}`)} closeDrawer={closeDrawer} sidebarCollapsed={sidebarCollapsed} />)}

        <DocsGroup t={t} closeDrawer={closeDrawer} sidebarCollapsed={sidebarCollapsed} />

        {NAV_REST.map(item => <NavItem key={item.to} item={item} label={t(`nav.${item.key}`)} closeDrawer={closeDrawer} sidebarCollapsed={sidebarCollapsed} />)}

        {user?.role === 'admin' && (
          <>
            {!sidebarCollapsed && (
              <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1px', color: 'var(--sidebar-text-muted)', padding: '14px 10px 6px' }}>
                {t('nav.admin')}
              </div>
            )}
            {sidebarCollapsed && <div style={{ height: 1, background: 'var(--sidebar-border)', margin: '10px 6px' }} />}
            {ADMIN_NAV.map(item => (
              <NavItem key={item.to} item={item} label={t(`nav.${item.key}`)} closeDrawer={closeDrawer}
                sidebarCollapsed={sidebarCollapsed}
                badge={item.key === 'supportCases' && supportCount > 0 ? supportCount : null} />
            ))}
          </>
        )}
      </nav>

      {/* Bottom — only collapse + build (account/lang/theme/full/signout moved to header) */}
      <div style={{ padding: '12px 8px', borderTop: '1px solid var(--sidebar-border)' }}>
        {!isMobile && (
          <button onClick={() => setCollapsed(c => !c)} style={{
            width: '100%', padding: '7px 10px', background: 'rgba(255,255,255,0.05)',
            border: 'none', borderRadius: 8, color: 'var(--sidebar-text-muted)', cursor: 'pointer',
            marginBottom: 6, fontSize: 12, display: 'flex', alignItems: 'center',
            justifyContent: collapsed ? 'center' : 'flex-start', gap: 8
          }}>
            <span>{collapsed ? '▶' : '◀'}</span>
            {!collapsed && t('nav.collapse')}
          </button>
        )}
        {!sidebarCollapsed && (
          <div style={{ textAlign: 'center', marginTop: 4, fontSize: 10, color: 'rgba(255,255,255,0.2)' }}>
            Build {versionInfo.build}
          </div>
        )}
      </div>
    </aside>
  );

  // ── Top header bar (account + lang + theme + full + signout) ──
  const headerBar = (
    <header style={{
      position: 'sticky', top: 0, zIndex: 100,
      background: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: 12,
      padding: isMobile ? '8px 10px' : '10px 14px',
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      gap: 8, marginBottom: 20,
      boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
    }}>
      {/* Left: mobile menu + quick create */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        {isMobile && (
          <button onClick={() => setDrawerOpen(true)} aria-label="Open menu" style={{
            background: 'transparent', border: '1px solid var(--border)', borderRadius: 8,
            padding: '7px 10px', cursor: 'pointer', color: 'var(--text)',
            display: 'flex', alignItems: 'center', gap: 6, fontSize: 14, position: 'relative',
          }}>
            <span style={{ fontSize: 16 }}>☰</span>
            {mobileUnread > 0 && (
              <span style={{
                position: 'absolute', top: -4, insetInlineEnd: -4,
                width: 10, height: 10, borderRadius: '50%',
                background: 'var(--danger)', border: '2px solid var(--card-bg)',
              }} />
            )}
          </button>
        )}
        <NewDocMenu t={t} isMobile={isMobile} />
      </div>

      {/* Right: version, account, language, theme, fullscreen, sign out */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
        <span title={`Build ${versionInfo.build}`} style={{
          fontSize: 11, fontWeight: 600, color: 'var(--text-muted)',
          background: 'var(--bg)', border: '1px solid var(--border)',
          borderRadius: 6, padding: '4px 8px', whiteSpace: 'nowrap',
        }}>
          v{versionInfo.version}
        </span>

        <NotificationBell />

        {/* Account — opens the profile page */}
        <button onClick={() => navigate('/profile')} title={t('nav.viewProfile')} aria-label={t('nav.viewProfile')}
          onMouseEnter={e => { e.currentTarget.style.background = 'var(--hover-bg)'; e.currentTarget.style.borderColor = 'var(--accent)'; }}
          onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.borderColor = 'var(--border)'; }}
          style={{
            background: 'transparent', border: '1px solid var(--border)', borderRadius: 8,
            padding: '5px 8px 5px 5px', cursor: 'pointer', color: 'var(--text)',
            display: 'flex', alignItems: 'center', gap: 8, fontFamily: 'inherit',
            transition: 'background 0.15s, border-color 0.15s',
          }}>
          {user?.avatarUrl ? (
            <img src={user.avatarUrl} alt="" style={{ width: 26, height: 26, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />
          ) : (
            <div style={{
              width: 26, height: 26, borderRadius: '50%',
              background: 'linear-gradient(135deg, #F59E0B, #FBBF24)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 11, fontWeight: 700, color: '#1A3C6E', flexShrink: 0,
            }}>{initials}</div>
          )}
          {!isMobile && (
            <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', lineHeight: 1.15, maxWidth: 140 }}>
              <span style={{ fontSize: 13, fontWeight: 600, maxWidth: 140, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {user?.name}
              </span>
              <span style={{ fontSize: 10, color: 'var(--text-subtle)', whiteSpace: 'nowrap' }}>{t('nav.viewProfile')}</span>
            </span>
          )}
          <span aria-hidden style={{ fontSize: 12, color: 'var(--text-subtle)', marginInlineStart: 2 }}>{isRTL ? '‹' : '›'}</span>
        </button>

        <LanguageSwitcher compact={isMobile} />

        <button onClick={toggleTheme} aria-label="Toggle theme" style={iconBtnLight}
          title={theme === 'dark' ? t('nav.light') : t('nav.dark')}>
          {theme === 'dark' ? '☀' : '🌙'}
        </button>

        <button onClick={toggleFullscreen} aria-label="Toggle fullscreen" style={iconBtnLight}
          title={isFullscreen ? t('nav.exit') : t('nav.full')}>
          {isFullscreen ? '⤢' : '⛶'}
        </button>

        <button onClick={handleLogout} title={t('nav.signOut')} style={{
          ...iconBtnLight, color: 'var(--danger)', borderColor: 'rgba(239,68,68,0.3)',
        }}>
          <span style={{ fontSize: 14 }}>⎋</span>
          {!isMobile && <span style={{ fontSize: 13, fontWeight: 600, marginInlineStart: 6 }}>{t('nav.signOut')}</span>}
        </button>
      </div>
    </header>
  );

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: 'var(--bg)', color: 'var(--text)' }}>
      {sidebar}

      {isMobile && drawerOpen && (
        <div onClick={closeDrawer} style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000
        }} />
      )}

      <main style={{
        flex: 1, minWidth: 0,
        padding: isMobile ? '16px 16px 24px' : '28px 32px',
        overflowY: 'auto'
      }}>
        {headerBar}

        {impersonating && (
          <div style={{
            background: 'rgba(139,92,246,0.12)', border: '1px solid rgba(139,92,246,0.45)',
            borderRadius: 10, padding: '10px 14px', marginBottom: 16,
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            gap: 12, flexWrap: 'wrap',
          }}>
            <div style={{ fontSize: 13, color: 'var(--text)', flex: '1 1 240px' }}>
              <span style={{ marginInlineEnd: 6 }}>🕵️</span>
              <b style={{ color: '#8B5CF6' }}>{t('impersonate.banner')}</b>
              {' — '}<b>{user?.name}</b> ({user?.email})
            </div>
            <button onClick={endImpersonation} style={{
              background: '#8B5CF6', color: '#fff', border: 'none', borderRadius: 6,
              padding: '6px 14px', fontWeight: 600, fontSize: 12, cursor: 'pointer',
            }}>
              {t('impersonate.return')}
            </button>
          </div>
        )}

        {updateAvailable && (
          <div style={{
            background: 'rgba(34,197,94,0.12)', border: '1px solid rgba(34,197,94,0.4)',
            borderRadius: 10, padding: '10px 14px', marginBottom: 16,
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            gap: 12, flexWrap: 'wrap',
          }}>
            <div style={{ fontSize: 13, color: 'var(--text)', flex: '1 1 240px' }}>
              <span style={{ marginInlineEnd: 6 }}>🎉</span>
              <b style={{ color: 'var(--success)' }}>{t('banner.updateAvailable') || 'Update available'}</b>
              {' — '}
              {(t('banner.newVersionReady') || 'A new version (v{v}) is ready. Refresh to load the latest.').replace('{v}', latest.version)}
            </div>
            <button onClick={reloadForUpdate} style={{
              background: 'var(--success)', color: '#fff', border: 'none', borderRadius: 6,
              padding: '6px 14px', fontWeight: 600, fontSize: 12, cursor: 'pointer',
            }}>
              {t('banner.refreshNow') || 'Refresh now'}
            </button>
          </div>
        )}

        {user && !user.emailVerified && (
          <div style={{
            background: 'rgba(245,158,11,0.12)', border: '1px solid rgba(245,158,11,0.4)',
            borderRadius: 10, padding: '10px 14px', marginBottom: 16,
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            gap: 12, flexWrap: 'wrap',
          }}>
            <div style={{ fontSize: 13, color: 'var(--text)', flex: '1 1 240px' }}>
              <b style={{ color: 'var(--accent)' }}>{t('banner.verifyEmail')}</b> {t('banner.weSentLink')} <b>{user.email}</b>.
              {resentMsg && <span style={{ marginInlineStart: 8, color: 'var(--text-muted)' }}>{resentMsg}</span>}
            </div>
            <button onClick={resendVerification} disabled={resending} style={{
              background: 'var(--accent)', color: '#1A3C6E', border: 'none', borderRadius: 6,
              padding: '6px 14px', fontWeight: 600, fontSize: 12, cursor: 'pointer',
              opacity: resending ? 0.6 : 1,
            }}>
              {resending ? t('banner.sending') : t('banner.resend')}
            </button>
          </div>
        )}

        {notice?.type === 'trial_extended' && (
          <div style={{
            background: 'rgba(34,197,94,0.12)', border: '1px solid rgba(34,197,94,0.4)',
            borderRadius: 10, padding: '10px 14px', marginBottom: 16,
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            gap: 12, flexWrap: 'wrap',
          }}>
            <div style={{ fontSize: 13, color: 'var(--text)', flex: '1 1 240px' }}>
              <span style={{ marginInlineEnd: 6 }}>🎉</span>
              <b style={{ color: 'var(--success)' }}>{t('banner.trialExtended') || 'Your trial has been extended'}</b>
              {' — '}
              {(t('banner.trialExtendedBy') || 'we added {d} extra day(s). Your trial now ends on {date}.')
                .replace('{d}', notice.days)
                .replace('{date}', notice.newTrialEndsAt ? new Date(notice.newTrialEndsAt).toLocaleDateString() : '—')}
            </div>
            <button onClick={dismissNotice} style={{
              background: 'transparent', color: 'var(--text-muted)', border: '1px solid var(--border)',
              borderRadius: 6, padding: '6px 14px', fontWeight: 600, fontSize: 12, cursor: 'pointer',
            }}>
              {t('banner.dismiss') || 'Dismiss'}
            </button>
          </div>
        )}

        {isPastDue && (
          <div style={{
            background: 'rgba(245,158,11,0.12)', border: '1px solid rgba(245,158,11,0.4)',
            borderRadius: 10, padding: '10px 14px', marginBottom: 16,
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            gap: 12, flexWrap: 'wrap',
          }}>
            <div style={{ fontSize: 13, color: 'var(--text)', flex: '1 1 240px' }}>
              <b style={{ color: '#D97706' }}>{t('banner.paymentFailed') || 'Payment failed'}</b>
              {' — '}
              {(t('banner.paymentFailedMsg') || "we couldn't charge your card. Update your payment method within {d} day(s) to keep access.")
                .replace('{d}', payGraceDaysLeft)}
            </div>
            <button onClick={openBillingPortal} style={{
              background: '#D97706', color: '#fff', border: 'none', borderRadius: 6,
              padding: '6px 14px', fontWeight: 600, fontSize: 12, cursor: 'pointer',
            }}>
              {t('banner.updatePayment') || 'Update payment method'}
            </button>
          </div>
        )}

        {inGrace && (
          <div style={{
            background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.4)',
            borderRadius: 10, padding: '10px 14px', marginBottom: 16,
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            gap: 12, flexWrap: 'wrap',
          }}>
            <div style={{ fontSize: 13, color: 'var(--text)', flex: '1 1 240px' }}>
              <b style={{ color: 'var(--danger)' }}>{t('banner.trialEnded') || 'Your trial has ended'}</b>
              {' — '}
              {(t('banner.graceDaysLeft') || 'you have {d} day(s) of grace access left. Subscribe to keep using Invio.')
                .replace('{d}', graceDaysLeft)}
            </div>
            <button onClick={() => navigate('/subscription')} style={{
              background: 'var(--danger)', color: '#fff', border: 'none', borderRadius: 6,
              padding: '6px 14px', fontWeight: 600, fontSize: 12, cursor: 'pointer',
            }}>
              {t('banner.subscribeNow') || 'Subscribe now'}
            </button>
          </div>
        )}

        {children}
      </main>
    </div>
  );
}

// "+ New" quick create — every document type one click away from any page
function NewDocMenu({ t, isMobile }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const ref = React.useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDown = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const create = type => { setOpen(false); navigate(`/documents/new?type=${type}`); };

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button onClick={() => setOpen(o => !o)} aria-expanded={open} style={{
        background: 'var(--heading, #1A3C6E)', color: '#fff', border: 'none', borderRadius: 8,
        padding: isMobile ? '7px 12px' : '8px 14px', cursor: 'pointer',
        display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 600,
        fontFamily: 'inherit',
      }}>
        <span style={{ fontSize: 14, lineHeight: 1 }}>＋</span>
        {!isMobile && <span>{t('nav.newDoc')}</span>}
        <span style={{ fontSize: 9, opacity: 0.8 }}>▾</span>
      </button>

      {open && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 6px)', insetInlineStart: 0, zIndex: 1100,
          background: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: 10,
          boxShadow: '0 12px 32px rgba(0,0,0,0.18)', minWidth: 200, padding: 4,
        }}>
          {DOC_NAV.filter(d => d.type).map(d => (
            <button key={d.type} onClick={() => create(d.type)} style={{
              width: '100%', textAlign: 'start', background: 'transparent', border: 'none',
              cursor: 'pointer', padding: '8px 12px', borderRadius: 6, fontFamily: 'inherit',
              display: 'flex', alignItems: 'center', gap: 10, color: 'var(--text)', fontSize: 13,
            }}
              onMouseEnter={e => { e.currentTarget.style.background = 'var(--hover-bg)'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}>
              <span style={{ fontSize: 15 }}>{d.icon}</span>
              {t(`docType.${d.type}`)}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function DocsGroup({ t, closeDrawer, sidebarCollapsed }) {
  const { pathname } = useLocation();
  const { user } = useAuth();
  const childActive = DOC_NAV.some(d => pathname === d.to) || pathname.startsWith('/documents');
  const [open, setOpen] = useState(() =>
    localStorage.getItem('navDocsOpen') !== null
      ? localStorage.getItem('navDocsOpen') === 'true'
      : true
  );
  const [hover, setHover] = useState(false);
  const [counts, setCounts] = useState(null); // { counts: {type: n}, total }
  const toggle = () => setOpen(o => { localStorage.setItem('navDocsOpen', String(!o)); return !o; });

  // Per-type counts; refreshed on navigation so create/delete show up.
  // Skipped for lapsed accounts — the gated endpoint would 402-redirect them.
  useEffect(() => {
    if (user?.hasActiveAccess === false) return;
    let cancelled = false;
    api.get('/documents/counts')
      .then(r => { if (!cancelled) setCounts(r.data); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [pathname, user?.hasActiveAccess]);

  const countFor = d => (counts == null ? null : (d.type ? counts.counts[d.type] || 0 : counts.total));

  // Icon-only sidebar: one 📁 entry that goes to the combined list
  if (sidebarCollapsed) {
    return <NavItem item={{ to: '/documents', icon: '📁' }} label="" closeDrawer={closeDrawer} sidebarCollapsed />;
  }

  return (
    <div>
      {/* Dropdown-style header: boxed row with item count and a big caret at
          the end that flips — unmistakably expandable, not a page link */}
      <button onClick={toggle} title={t('nav.docGroup')}
        onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}
        aria-expanded={open}
        style={{
          width: '100%', display: 'flex', alignItems: 'center', gap: 10,
          padding: '9px 10px', borderRadius: 8, marginBottom: 2,
          background: hover ? 'rgba(255,255,255,0.08)' : 'rgba(255,255,255,0.04)',
          border: '1px solid var(--sidebar-border)',
          cursor: 'pointer', fontFamily: 'inherit', fontSize: 14,
          color: childActive && !open ? '#F59E0B' : 'var(--sidebar-text)',
          fontWeight: 600,
          whiteSpace: 'nowrap', overflow: 'hidden', transition: 'background 0.15s',
        }}>
        <span style={{ fontSize: 16, flexShrink: 0 }}>{open ? '📂' : '📁'}</span>
        <span style={{ flex: 1, textAlign: 'start' }}>{t('nav.docGroup')}</span>
        <span style={{
          fontSize: 10, fontWeight: 700, flexShrink: 0,
          background: '#F59E0B', color: '#1A3C6E',
          borderRadius: 20, padding: '1px 7px',
        }}>{DOC_NAV.length}</span>
        <span style={{
          fontSize: 12, flexShrink: 0, display: 'inline-block',
          transition: 'transform 0.15s', transform: open ? 'rotate(180deg)' : 'none',
          color: 'var(--sidebar-text-muted)',
        }}>▼</span>
      </button>

      {open && DOC_NAV.map(d => (
        <NavLink key={d.to} to={d.to} onClick={closeDrawer}
          style={({ isActive }) => ({
            display: 'flex', alignItems: 'center', gap: 9,
            padding: '7px 10px', paddingInlineStart: 26, borderRadius: 8, marginBottom: 1,
            color: isActive ? '#F59E0B' : 'var(--sidebar-text)',
            background: isActive ? 'rgba(245,158,11,0.12)' : 'transparent',
            fontWeight: isActive ? 600 : 400, fontSize: 13,
            transition: 'all 0.15s', whiteSpace: 'nowrap', overflow: 'hidden',
            textDecoration: 'none',
          })}>
          <span style={{ fontSize: 14, flexShrink: 0 }}>{d.icon}</span>
          <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {d.type ? t(`docs.titlePlural.${d.type}`) : t('docs.allDocuments')}
          </span>
          {countFor(d) > 0 && (
            <span style={{
              fontSize: 10, fontWeight: 700, flexShrink: 0,
              background: 'rgba(245,158,11,0.18)', color: '#F59E0B',
              borderRadius: 20, padding: '1px 7px',
            }}>
              {countFor(d)}
            </span>
          )}
        </NavLink>
      ))}
    </div>
  );
}

function NavItem({ item, label, closeDrawer, sidebarCollapsed, badge = null }) {
  return (
    <NavLink to={item.to} end={item.to === '/'} onClick={closeDrawer}
      style={({ isActive }) => ({
        display: 'flex', alignItems: 'center', gap: 10,
        padding: '9px 10px', borderRadius: 8, marginBottom: 2,
        color: isActive ? '#F59E0B' : 'var(--sidebar-text)',
        background: isActive ? 'rgba(245,158,11,0.12)' : 'transparent',
        fontWeight: isActive ? 600 : 400, fontSize: 14,
        transition: 'all 0.15s', whiteSpace: 'nowrap', overflow: 'hidden',
        textDecoration: 'none'
      })}>
      <span style={{ fontSize: 16, flexShrink: 0, position: 'relative' }}>
        {item.icon}
        {badge != null && sidebarCollapsed && (
          <span style={{
            position: 'absolute', top: -3, insetInlineEnd: -5, width: 8, height: 8,
            borderRadius: '50%', background: 'var(--danger)',
          }} />
        )}
      </span>
      {!sidebarCollapsed && (
        <>
          <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>{label || item.label}</span>
          {badge != null && (
            <span style={{
              fontSize: 10, fontWeight: 700, flexShrink: 0,
              background: 'var(--danger)', color: '#fff',
              borderRadius: 20, padding: '1px 7px', minWidth: 17, textAlign: 'center',
            }}>{badge > 99 ? '99+' : badge}</span>
          )}
        </>
      )}
    </NavLink>
  );
}

const iconBtnLight = {
  background: 'transparent', border: '1px solid var(--border)', borderRadius: 8,
  padding: '6px 10px', cursor: 'pointer', color: 'var(--text)',
  fontSize: 14, minWidth: 34, fontFamily: 'inherit',
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
};
